import React, { useEffect, useState, useRef } from "react";
import Choices from "choices.js";
import axios from "axios";
import { useGlobalStateStore } from "../../stores/store";
import { API_URL } from "../../constants";

const LeaguesSelect = ({ label = "League" }) => {
  const [leagues, setLeagues] = useState([]);
  const selectRef = useRef(null);
  const choicesRef = useRef(null);
  const { filterOpen } = useGlobalStateStore(); // Access filterOpen state
  const store = useGlobalStateStore();
  const selectedLeagues = useGlobalStateStore((state) => state.filter.league);

  const onChange = () => {
    const ref = selectRef.current;
    const selectedValues = ref
      ? Array.from(ref.selectedOptions).map((option) => option.value)
      : [];
    store.setLeague(selectedValues);

    const choicesListMultiple = ref.parentElement.querySelector(
      ".choices__list.choices__list--multiple"
    );
    if (choicesListMultiple) {
      choicesListMultiple.classList.toggle(
        "has-values",
        selectedValues.length > 0
      );
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchLeagues = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v1/leagues/`);
        if (isMounted) {
          const leaguesList = response.data.map((league) => ({
            value: league.id,
            label: league.name,
          }));
          setLeagues(leaguesList);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch leagues:", error);
        }
      }
    };

    if (filterOpen) {
      fetchLeagues(); // Fetch only when filter panel is open
    }

    return () => {
      isMounted = false; // Prevent state updates after unmount
    };
  }, [filterOpen]); // Re-run when filterOpen changes

  useEffect(() => {
    if (selectRef.current && leagues.length > 0) {
      if (choicesRef.current) {
        choicesRef.current.destroy();
      }

      choicesRef.current = new Choices(selectRef.current, {
        searchEnabled: true,
        removeItemButton: true,
        placeholder: true,
        placeholderValue: "Select",
        noResultsText: "No leagues found",
        noChoicesText: "No options available",
        itemSelectText: "",
        shouldSort: false,
        searchFields: ["label"],
        fuseOptions: {
          threshold: 0.1,
          ignoreLocation: false,
          location: 0,
          distance: 0,
          minMatchCharLength: 1,
        },
        searchPlaceholderValue: "Type to search...",
        searchResultLimit: 1000,
        searchChoices(value) {
          if (!value) return this._store.choices.filter((item) => item.active);
          return this._store.choices.filter(
            (item) =>
              item.active &&
              item.label.toLowerCase().startsWith(value.toLowerCase())
          );
        },
      });

      choicesRef.current.setChoices(leagues, "value", "label", true);

      // Visually reselect values from Zustand store
      setTimeout(() => {
        choicesRef.current.removeActiveItems();
        selectedLeagues.forEach((id) => {
          choicesRef.current.setChoiceByValue(id);
        });
      }, 0);

      // Dropdown interaction tweaks
      const dropdown =
        selectRef.current.parentElement.parentElement.querySelector(
          ".choices__list--dropdown"
        );
      const searchInput = selectRef.current.parentElement.querySelector(
        "input.choices__input"
      );

      if (dropdown && searchInput) {
        dropdown.insertBefore(searchInput, dropdown.firstChild);

        dropdown.addEventListener("focusout", (event) => {
          if (!dropdown.contains(event.relatedTarget)) {
            searchInput.value = "";
            const inputEvent = new Event("input", { bubbles: true });
            searchInput.dispatchEvent(inputEvent);
          }
        });

        const choicesContainer = selectRef.current.closest(".choices");
        const innerContainer =
          choicesContainer.querySelector(".choices__inner");

        if (innerContainer) {
          innerContainer.addEventListener("click", (event) => {
            const dropdownIsOpen =
              choicesContainer.classList.contains("is-open");

            document
              .querySelectorAll(".choices.is-open")
              .forEach((openDropdown) => {
                if (openDropdown !== choicesContainer) {
                  openDropdown.classList.remove("is-open");
                  const otherDropdownList = openDropdown.querySelector(
                    ".choices__list--dropdown"
                  );
                  if (otherDropdownList) {
                    otherDropdownList.classList.remove("is-active");
                  }
                }
              });

            if (!dropdownIsOpen) {
              choicesRef.current.showDropdown();
              dropdown.classList.add("is-active");
            } else {
              choicesRef.current.hideDropdown();
              dropdown.classList.remove("is-active");
            }

            event.stopPropagation();
          });
        }
      }
    }

    return () => {
      if (choicesRef.current) {
        choicesRef.current.destroy();
        choicesRef.current = null;
      }
    };
  }, [leagues, selectedLeagues]);

  return (
    <div className="filter-select-wrap">
      <label htmlFor="leagueSelect">{label}</label>
      <select
        id="leagueSelect"
        multiple
        ref={selectRef}
        onChange={onChange}
        className="display-content"
      ></select>
    </div>
  );
};

export default LeaguesSelect;
